/**
 * SoundEngine — Web Audio API synthesis engine (zero file downloads)
 * Singleton with lazy AudioContext init on first user gesture.
 *
 * Gain routing:
 *   AudioContext → masterGain → sfxGain       → destination
 *                              → ambientGain   → destination
 *                              → companionGain → destination
 */

type StopFn = () => void;

export class SoundEngine {
  private static instance: SoundEngine | null = null;

  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private companionGain: GainNode | null = null;

  private ambientStopFn: StopFn | null = null;
  private ambientDuckLevel = 1;

  private constructor() {}

  static getInstance(): SoundEngine {
    if (!SoundEngine.instance) {
      SoundEngine.instance = new SoundEngine();
    }
    return SoundEngine.instance;
  }

  // --- Init (must be called from a user gesture the first time) ---

  private ensureContext(): AudioContext | null {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') {
        this.ctx.resume().catch(() => {});
      }
      return this.ctx;
    }

    try {
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0.7;
      this.masterGain.connect(this.ctx.destination);

      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = 0.8;
      this.sfxGain.connect(this.masterGain);

      this.ambientGain = this.ctx.createGain();
      this.ambientGain.gain.value = 0.3;
      this.ambientGain.connect(this.masterGain);

      this.companionGain = this.ctx.createGain();
      this.companionGain.gain.value = 0.6;
      this.companionGain.connect(this.masterGain);

      return this.ctx;
    } catch {
      return null;
    }
  }

  // --- Volume controls ---

  setMasterVolume(v: number) {
    if (this.masterGain) this.masterGain.gain.value = clamp01(v);
  }

  setSfxVolume(v: number) {
    if (this.sfxGain) this.sfxGain.gain.value = clamp01(v);
  }

  setAmbientVolume(v: number) {
    if (this.ambientGain) this.ambientGain.gain.value = clamp01(v) * this.ambientDuckLevel;
  }

  setCompanionVolume(v: number) {
    if (this.companionGain) this.companionGain.gain.value = clamp01(v);
  }

  duckAmbient() {
    this.ambientDuckLevel = 0.2;
    if (this.ambientGain) {
      this.ambientGain.gain.linearRampToValueAtTime(
        this.ambientGain.gain.value * 0.2,
        (this.ctx?.currentTime ?? 0) + 0.3,
      );
    }
  }

  unduckAmbient() {
    this.ambientDuckLevel = 1;
    if (this.ambientGain && this.ctx) {
      this.ambientGain.gain.linearRampToValueAtTime(
        this.ambientGain.gain.value / 0.2,
        this.ctx.currentTime + 0.3,
      );
    }
  }

  stopAmbient() {
    if (this.ambientStopFn) {
      this.ambientStopFn();
      this.ambientStopFn = null;
    }
  }

  dispose() {
    this.stopAmbient();
    if (this.ctx) {
      this.ctx.close().catch(() => {});
      this.ctx = null;
    }
    this.masterGain = null;
    this.sfxGain = null;
    this.ambientGain = null;
    this.companionGain = null;
    SoundEngine.instance = null;
  }

  // ===================== INTERACTION SOUNDS =====================

  /** Two sine oscillators (800Hz + 1200Hz), 5ms attack, 80ms decay, slight pitch bend */
  buttonPop() {
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;
    const t = ctx.currentTime;

    for (const freq of [800, 1200]) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);
      osc.frequency.linearRampToValueAtTime(freq * 1.05, t + 0.08);
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.3, t + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
      osc.connect(gain).connect(this.sfxGain);
      osc.start(t);
      osc.stop(t + 0.1);
    }
  }

  /** White noise burst through high-pass filter (2kHz), 3ms attack, 30ms decay */
  toggleClick() {
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;
    const t = ctx.currentTime;

    const bufferSize = ctx.sampleRate * 0.04;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 2000;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.25, t + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.04);

    source.connect(hp).connect(gain).connect(this.sfxGain);
    source.start(t);
    source.stop(t + 0.04);
  }

  /** Filtered white noise, band-pass sweep 200→2000Hz, stereo panner */
  navigationWhoosh() {
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;
    const t = ctx.currentTime;

    const bufferSize = ctx.sampleRate * 0.25;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(200, t);
    bp.frequency.exponentialRampToValueAtTime(2000, t + 0.2);
    bp.Q.value = 2;

    const panner = ctx.createStereoPanner();
    panner.pan.setValueAtTime(-0.5, t);
    panner.pan.linearRampToValueAtTime(0.5, t + 0.25);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.15, t + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);

    source.connect(bp).connect(panner).connect(gain).connect(this.sfxGain);
    source.start(t);
    source.stop(t + 0.25);
  }

  // ===================== CELEBRATION SOUNDS =====================

  /** 2-note ascending sine (C5→E5: 523→659Hz), each 120ms */
  taskChime() {
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;
    const t = ctx.currentTime;
    const dest = this.sfxGain;

    const notes = [523, 659];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = t + i * 0.12;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.35, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.12);
      osc.connect(gain).connect(dest);
      osc.start(start);
      osc.stop(start + 0.12);
    });
  }

  /** 4-note arpeggio (C5,E5,G5,C6), triangle wave, staggered 80ms */
  routineJingle() {
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;
    const t = ctx.currentTime;
    const dest = this.sfxGain;

    const notes = [523, 659, 784, 1047];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      const start = t + i * 0.08;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.3, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.2);
      osc.connect(gain).connect(dest);
      osc.start(start);
      osc.stop(start + 0.2);
    });
  }

  /** 6-note ascending fanfare (C4→G5), sawtooth + low-pass, sparkle noise overlay */
  streakFanfare() {
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;
    const t = ctx.currentTime;

    const notes = [262, 330, 392, 523, 659, 784];
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 3000;
    lp.connect(this.sfxGain);

    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.value = freq;
      const start = t + i * 0.1;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.2, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.3);
      osc.connect(gain).connect(lp);
      osc.start(start);
      osc.stop(start + 0.3);
    });

    // Sparkle noise overlay
    const noiseLen = ctx.sampleRate * 0.8;
    const buf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < noiseLen; i++) d[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 4000;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0, t);
    ng.gain.linearRampToValueAtTime(0.08, t + 0.2);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.8);
    noise.connect(hp).connect(ng).connect(this.sfxGain);
    noise.start(t);
    noise.stop(t + 0.8);
  }

  /** 5-8 random high-freq sine bursts (1000-3000Hz), 30ms each, scattered over 500ms */
  rewardSparkle() {
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;
    const t = ctx.currentTime;

    const count = 5 + Math.floor(Math.random() * 4);
    for (let i = 0; i < count; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 1000 + Math.random() * 2000;
      const start = t + Math.random() * 0.47;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.2, start + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.03);
      osc.connect(gain).connect(this.sfxGain);
      osc.start(start);
      osc.stop(start + 0.03);
    }
  }

  // ===================== FEEDBACK SOUNDS =====================

  /** Single sine at G5 (784Hz), 10ms attack, 300ms decay */
  successDing() {
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 784;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.4, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    osc.connect(gain).connect(this.sfxGain);
    osc.start(t);
    osc.stop(t + 0.35);
  }

  /** Two detuned square waves (150+155Hz) for gentle beat freq, 200ms decay */
  errorBonk() {
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;
    const t = ctx.currentTime;

    for (const freq of [150, 155]) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.12, t + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      osc.connect(gain).connect(this.sfxGain);
      osc.start(t);
      osc.stop(t + 0.25);
    }
  }

  // ===================== COMPANION SOUNDS =====================

  /** Sine 300Hz with 6Hz vibrato (LFO ±10Hz), 100ms attack, 200ms sustain, 150ms release */
  happyHum() {
    const ctx = this.ensureContext();
    if (!ctx || !this.companionGain) return;
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.value = 300;
    lfo.type = 'sine';
    lfo.frequency.value = 6;
    lfoGain.gain.value = 10;

    lfo.connect(lfoGain).connect(osc.frequency);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.3, t + 0.1);
    gain.gain.setValueAtTime(0.3, t + 0.3);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);

    osc.connect(gain).connect(this.companionGain);
    osc.start(t);
    lfo.start(t);
    osc.stop(t + 0.45);
    lfo.stop(t + 0.45);
  }

  /** Two-part: sine 350Hz (150ms), 30ms gap, sine 380Hz (120ms), slight vibrato */
  encouragingMmHmm() {
    const ctx = this.ensureContext();
    if (!ctx || !this.companionGain) return;
    const t = ctx.currentTime;

    const parts: [number, number, number][] = [
      [350, t, 0.15],
      [380, t + 0.18, 0.12],
    ];

    parts.forEach(([freq, start, dur]) => {
      const osc = ctx.createOscillator();
      const lfo = ctx.createOscillator();
      const lfoG = ctx.createGain();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.value = freq;
      lfo.type = 'sine';
      lfo.frequency.value = 5;
      lfoG.gain.value = 5;

      lfo.connect(lfoG).connect(osc.frequency);
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.25, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, start + dur);

      osc.connect(gain).connect(this.companionGain!);
      osc.start(start);
      lfo.start(start);
      osc.stop(start + dur);
      lfo.stop(start + dur);
    });
  }

  /** Pitch sweep sine 400→800Hz (300ms) + second oscillator 600→1200Hz delayed 100ms */
  celebratoryWoohoo() {
    const ctx = this.ensureContext();
    if (!ctx || !this.companionGain) return;
    const t = ctx.currentTime;

    const sweeps: [number, number, number, number][] = [
      [400, 800, t, 0.3],
      [600, 1200, t + 0.1, 0.3],
    ];

    sweeps.forEach(([startFreq, endFreq, start, dur]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(startFreq, start);
      osc.frequency.exponentialRampToValueAtTime(endFreq, start + dur);
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + dur + 0.1);
      osc.connect(gain).connect(this.companionGain!);
      osc.start(start);
      osc.stop(start + dur + 0.1);
    });
  }

  /** Sine 500→400→450Hz curve over 300ms, slight vibrato. Curious intonation. */
  gentleOh() {
    const ctx = this.ensureContext();
    if (!ctx || !this.companionGain) return;
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    const lfo = ctx.createOscillator();
    const lfoG = ctx.createGain();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(500, t);
    osc.frequency.linearRampToValueAtTime(400, t + 0.15);
    osc.frequency.linearRampToValueAtTime(450, t + 0.3);

    lfo.type = 'sine';
    lfo.frequency.value = 5;
    lfoG.gain.value = 8;
    lfo.connect(lfoG).connect(osc.frequency);

    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.25, t + 0.02);
    gain.gain.setValueAtTime(0.25, t + 0.2);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);

    osc.connect(gain).connect(this.companionGain);
    osc.start(t);
    lfo.start(t);
    osc.stop(t + 0.35);
    lfo.stop(t + 0.35);
  }

  // ===================== AMBIENT SOUNDSCAPES =====================

  /** Random sine chirps (1000-3000Hz) at 2-5s intervals + low filtered brown noise */
  startMorningAmbient(): StopFn {
    this.stopAmbient();
    const ctx = this.ensureContext();
    if (!ctx || !this.ambientGain) return () => {};

    let stopped = false;

    // Brown noise (filtered)
    const bufLen = ctx.sampleRate * 2;
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const d = buf.getChannelData(0);
    let lastOut = 0;
    for (let i = 0; i < bufLen; i++) {
      const white = Math.random() * 2 - 1;
      lastOut = (lastOut + 0.02 * white) / 1.02;
      d[i] = lastOut * 3.5;
    }
    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = buf;
    noiseSource.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 300;
    const ng = ctx.createGain();
    ng.gain.value = 0.15;
    noiseSource.connect(lp).connect(ng).connect(this.ambientGain);
    noiseSource.start();

    // Chirps
    const chirp = () => {
      if (stopped || !this.ambientGain) return;
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 1000 + Math.random() * 2000;
      const dur = 0.05 + Math.random() * 0.05;
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.1, now + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, now + dur);
      osc.connect(g).connect(this.ambientGain);
      osc.start(now);
      osc.stop(now + dur);
      const next = 2000 + Math.random() * 3000;
      setTimeout(chirp, next);
    };
    setTimeout(chirp, 1000);

    const stop: StopFn = () => {
      stopped = true;
      try { noiseSource.stop(); } catch { /* already stopped */ }
    };
    this.ambientStopFn = stop;
    return stop;
  }

  /** Slow pentatonic melody (C4,D4,E4,G4,A4) on triangle wave, one note per 3-4s */
  startAfternoonAmbient(): StopFn {
    this.stopAmbient();
    const ctx = this.ensureContext();
    if (!ctx || !this.ambientGain) return () => {};

    let stopped = false;
    const pentatonic = [262, 294, 330, 392, 440];

    const playNote = () => {
      if (stopped || !this.ambientGain) return;
      const now = ctx.currentTime;
      const freq = pentatonic[Math.floor(Math.random() * pentatonic.length)];
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.12, now + 0.1);
      g.gain.exponentialRampToValueAtTime(0.001, now + 1.5);
      osc.connect(g).connect(this.ambientGain);
      osc.start(now);
      osc.stop(now + 1.5);
      const next = 3000 + Math.random() * 1000;
      setTimeout(playNote, next);
    };
    setTimeout(playNote, 500);

    const stop: StopFn = () => { stopped = true; };
    this.ambientStopFn = stop;
    return stop;
  }

  /** Pentatonic sine (C5,D5,E5,G5,A5) with quick attack + long decay (music box), one note per 2-3s */
  startEveningAmbient(): StopFn {
    this.stopAmbient();
    const ctx = this.ensureContext();
    if (!ctx || !this.ambientGain) return () => {};

    let stopped = false;
    const pentatonic = [523, 587, 659, 784, 880];

    const playNote = () => {
      if (stopped || !this.ambientGain) return;
      const now = ctx.currentTime;
      const freq = pentatonic[Math.floor(Math.random() * pentatonic.length)];
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.15, now + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, now + 2);
      osc.connect(g).connect(this.ambientGain);
      osc.start(now);
      osc.stop(now + 2);
      const next = 2000 + Math.random() * 1000;
      setTimeout(playNote, next);
    };
    setTimeout(playNote, 500);

    const stop: StopFn = () => { stopped = true; };
    this.ambientStopFn = stop;
    return stop;
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
